import React, { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { templatesAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import ConfirmationModal from '../components/UI/ConfirmationModal';
import {
  FileVideo,
  Trash2,
  AlertCircle,
  Play,
  Pause
} from 'lucide-react';

const ManageTemplatesPage = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [templateToDelete, setTemplateToDelete] = React.useState(null);
  const [playingVideo, setPlayingVideo] = React.useState(null);
  const videoRefs = useRef({});

  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['allTemplates'],
    queryFn: templatesAPI.getAll,
    enabled: isAdmin // Only fetch if user is admin
  });

  // Effect to handle video playback when playingVideo changes
  React.useEffect(() => {
    if (playingVideo && videoRefs.current[playingVideo]) {
      const video = videoRefs.current[playingVideo];
      video.play().catch(err => console.log('Video play failed:', err));
    }
  }, [playingVideo]);

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId) => templatesAPI.delete(templateId),
    onSuccess: () => {
      // Invalidate and refetch templates
      queryClient.invalidateQueries(['allTemplates']);
      queryClient.invalidateQueries(['templates']);
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    },
    onError: (error) => {
      console.error('Error deleting template:', error);
      alert('Failed to delete template. Please try again.');
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  });

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-ag-black-800 p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">You must be an admin to access this page.</p>
        </div>
      </div>
    );
  }

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      deleteTemplateMutation.mutate(templateToDelete._id);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handlePlayVideo = (e, templateId, previewUrl) => {
    e.stopPropagation();
    if (!previewUrl) return;

    if (playingVideo === templateId) {
      // Stop playing
      if (videoRefs.current[templateId]) {
        videoRefs.current[templateId].pause();
        videoRefs.current[templateId].currentTime = 0;
      }
      setPlayingVideo(null);
    } else {
      // Stop any currently playing video
      if (playingVideo && videoRefs.current[playingVideo]) {
        videoRefs.current[playingVideo].pause();
        videoRefs.current[playingVideo].currentTime = 0;
      }
      // Start playing new video
      setPlayingVideo(templateId);
    }
  };

  const templates = templatesData?.data?.data?.templates || templatesData?.data?.templates || [];

  return (
    <div className="min-h-screen bg-ag-black-800 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Manage Templates
          </h1>
          <p className="text-gray-400 mt-1">
            Delete templates and their associated media
          </p>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-ag-black-700 rounded-lg border border-ag-black-400 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-white">All Templates ({templates.length})</h2>
        </div>
        
        {templates.length === 0 ? (
          <div className="text-center py-16">
            <FileVideo className="mx-auto text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-medium text-white mb-2">
              No templates found
            </h3>
            <p className="text-gray-400">
              There are currently no templates in the system
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template._id}
                className="group bg-ag-black-600 rounded-lg border border-ag-black-400 overflow-hidden hover:border-red-500 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-ag-black-800 overflow-hidden">
                  {playingVideo === template._id && template.previewVideoUrl ? (
                    <video
                      ref={el => videoRefs.current[template._id] = el}
                      className="object-cover w-full h-full"
                      src={template.previewVideoUrl}
                      loop
                      playsInline
                      autoPlay
                    />
                  ) : template.thumbnailUrl ? (
                    <img
                      className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                      src={template.thumbnailUrl}
                      alt={template.name}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <FileVideo className="text-gray-600" size={48} />
                    </div>
                  )}
                  
                  {/* Play button - always visible if preview exists */}
                  {template.previewVideoUrl && (
                    <button
                      onClick={(e) => handlePlayVideo(e, template._id, template.previewVideoUrl)}
                      className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ag-green/90 hover:bg-ag-green border-2 border-ag-green text-ag-black-800 flex items-center justify-center transition-all hover:scale-110 shadow-lg shadow-ag-green/30 z-20"
                      aria-label={playingVideo === template._id ? "Pause preview" : "Play preview"}
                    >
                      {playingVideo === template._id ? (
                        <Pause size={16} fill="currentColor" />
                      ) : (
                        <Play size={16} fill="currentColor" className="ml-0.5" />
                      )}
                    </button>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleDeleteClick(template)}
                      className="p-3 bg-red-500/90 text-white hover:bg-red-600 rounded-lg transition-colors border border-red-400 flex items-center gap-2"
                      title="Delete template"
                    >
                      <Trash2 size={20} />
                      <span className="font-medium">Delete</span>
                    </button>
                  </div>
                </div>
                
                {/* Template Info */}
                <div className="p-4">
                  <h3 className="font-medium text-white truncate mb-1">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-400 truncate mb-2">
                    {template.description || 'No description'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Category: {template.category}</span>
                    <span>Uses: {template.usageCount || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        message={`Are you sure you want to delete "${templateToDelete?.name || 'this template'}"? This action cannot be undone and will delete all associated media files.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ManageTemplatesPage;